import { useMemo, useCallback, useEffect, useState } from 'react';
import { Col, Row } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';

import { useApi } from 'hooks/useApi';

import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';

import VacancyApplicationsAdapter from './vacancyApplications.adapter';
import VacancyApplicationsApi from 'libs/api/cv/vacancyApplications';
import CVFilters from 'libs/api/cv/cv';
import { useNavigate } from 'hooks/useNavigate';
import LinkWithParams from 'components/linkWithParams';
import WishlistsApi from 'libs/api/cv/wishlists';
import { useFilter } from '../../../../hooks/useFilter';

const api = new VacancyApplicationsApi();
const filtersApi = new CVFilters();
const wishlistsApi = new WishlistsApi();

const aditionalFunctionForSingleItemCall = (params) => {
  return !!params?.params?.shopping_center_id;
};

/**
 *
 * @param {boolean} isMainPage - true if the page loads all items, false if just one
 * @returns {{
 *  columns: any,
 *  data: any,
 *  singleItem: any,
 *  loading: boolean,
 *  updateInProcess: boolean,
 *  setUpdateInProcess: func,
 *  onDelete: func,
 *  onChangeTableFilter: func
 * }} state and callbacks for VacancyApplications page
 */
const useVacancyApplications = (isMainPage = true) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [aditionalParamsForSingle, setAditionalParamsForSingle] = useState({});
  const [statisticModalShow, setStatisticModalShow] = useState(false);
  const {
    loading,
    data,
    singleItem,
    onDelete,
    onChangeTableFilter,
    updateInProcess,
    handleDownloadExcel,
    searchParams,
    setUpdateInProcess,
  } = useApi({
    resourceKey: 'vacancy_applications',
    singleResourceKey: 'vacancy_application',
    api,
    isMainPage,
    adapter: VacancyApplicationsAdapter,
    aditionalParamsForSingle,
    aditionalFunctionForSingleItemCall,
  });
  const { filterData } = useFilter();
  const [filtersData, setFiltersData] = useState({
    vacancy_categories: [],
    experience_years: [],
    birthday: [],
    contract_types: [],
    regions: [],
    status: [],
    vacancy_application_types: [],
    vacancy_languages: [],
    birthday_range: [],
  });
  const handleDownloadPdf = useCallback(
    async (fileName = 'vacancy_application.pdf', type) => {
      setUpdateInProcess(true);
      try {
        const response = await api.getPDF(
          id,
          {
            shopping_center_id: searchParams.get('by_shopping_center'),
          },
          type,
        );
        Helpers.downloadFile(response, fileName);
        setStatisticModalShow(false);
      } catch (e) {
        UIHelper.serverError();
      } finally {
        setUpdateInProcess(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        render: (data, row) => row['shopping_center.title'],
      },
      {
        title: t('Insegna'),
        dataIndex: 'companies.title',
        render: (data, row) => row['vacancies.company_id'],
      },
      {
        title: t('ID Candidato'),
        dataIndex: 'users.id',
        sorter: () => {},
      },
      {
        title: t('Cognome'),
        dataIndex: 'users.surname',
        sorter: () => {},
      },
      {
        title: t('Nome'),
        dataIndex: 'users.name',
        sorter: () => {},
      },
      {
        title: t('Settore'),
        dataIndex: 'vacancy_application_types.title',
        render: (data, row) => row['vacancy_application_type_value'],
      },
      {
        title: t('Profilo candidato'),
        dataIndex: 'vacancy_categories.title',
        render: (data, row) => row['vacancy_category_title'],
      },
      {
        title: t('Sesso'),
        dataIndex: 'users.sex',
        sorter: () => {},
        render: (data, row) => row['sex_short'],
      },
      {
        title: t('Fascia d’età'),
        dataIndex: 'users.birthday_range',
        sorter: () => {},
        render: (data, row) => row['birthday_range'],
      },
      {
        title: t('Anni esp'),
        dataIndex: 'experience_years.title',
        render: (data, row) => row['experience_years'],
      },
      {
        title: t('Disponibilita` contratto'),
        dataIndex: 'vacancy_types.title',
        render: (data, row) => row['vacancy_type_title'],
      },
      {
        title: t('Provincia'),
        dataIndex: 'regions.title',
        render: (data, row) => row['region'],
      },

      {
        title: t('Lingua 1'),
        dataIndex: 'vacancy_languages.title',
        render: (data, row) => row['vacancy_language'],
      },
      {
        title: t('Livello lingua 1'),
        dataIndex: 'users.vacancy_language_level',
        sorter: () => {},
        render: (data, row) => row['vacancy_language_level'],
      },
      {
        title: t('Lingua 2'),
        dataIndex: 'vacancy_language2s_users.title',
        render: (data, row) => row['vacancy_language2'],
      },
      {
        title: t('Livello lingua 2'),
        dataIndex: 'users.vacancy_language2_level',
        sorter: () => {},
        render: (data, row) => row['vacancy_language2_level'],
      },
      {
        title: t('Whishlist'),
        dataIndex: 'wishlist',
        render: (data) => (data ? 'Si' : 'No'),
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 300,
        fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'center'}
            onClick={(e) => e.stopPropagation()}
          >
            <Col>
              <LinkWithParams to={`${pathname}/${row.id}/view`}>
                <Button icon={<EyeOutlined />}>Apri</Button>
              </LinkWithParams>
            </Col>
            <Col>
              <Link
                to={`${pathname.replace('vacancy_applications', 'vacancies')}/${
                  row.vacancy.uid
                }/edit/view`}
              >
                <Button type='secondary'>Visualizza annuncio</Button>
              </Link>
            </Col>
          </Row>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: filterData?.shopping_centers?.map((shopping_center) => ({
          title: shopping_center.title,
          value: shopping_center.value,
        })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company',
        isSearch: true,
        values: filterData?.companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
        inactiveValues: filterData?.inactive_companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
      },
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_text',
        values: [],
        isActiveSearch: true,
      },
      {
        title: t('Settore'),
        type: 'select',
        name: 'by_vacancy_application_type',
        values: filtersData.vacancy_application_types.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Profilo candidato'),
        type: 'select',
        name: 'by_vacancy_category_id',
        values: filtersData.vacancy_categories.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Sesso'),
        type: 'select',
        name: 'by_sex',
        values: [
          { value: 1, title: 'M' },
          { value: 2, title: 'F' },
          { value: 3, title: 'Non dichiara' },
        ],
      },
      {
        title: t('Fascia di età'),
        type: 'select',
        name: 'by_range_birthday',
        values: filtersData.birthday_range.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Anni di esperienza'),
        type: 'select',
        name: 'by_experience_years_id',
        values: filtersData.experience_years.map((item) => ({
          title: item.title,
          value: item.value,
        })),
      },
      {
        title: t('Disponibilità contratto'),
        type: 'select',
        name: 'by_contract_type_id',
        values: filtersData.contract_types.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Provincia'),
        type: 'select',
        name: 'by_region_id',
        isSearch: true,
        values: filtersData.regions.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Lingua 1'),
        type: 'select',
        name: 'by_vacancy_language_id',
        values: filtersData.vacancy_languages.map((item) => ({
          title: item.title,
          value: item.value,
        })),
      },
      {
        title: t('Livello lingua 1'),
        type: 'input',
        name: 'by_vacancy_language_level',
        values: [],
      },
      {
        title: t('Lingua 2'),
        type: 'select',
        name: 'by_vacancy_language2_id',
        values: filtersData.vacancy_languages.map((item) => ({
          title: item.title,
          value: item.value,
        })),
      },
      {
        title: t('Livello lingua 2'),
        type: 'input',
        name: 'by_vacancy_language2_level',
        values: [],
      },
      {
        title: t('Whishlist'),
        type: 'checkbox',
        name: 'is_wishlist',
      },
    ],
    [t, filtersData, filterData],
  );

  useEffect(() => {
    (async () => {
      try {
        const filters = await filtersApi.list();
        setFiltersData(filters.data);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  const handleAddWishList = async (data) => {
    setUpdateInProcess(true);
    try {
      const response = await wishlistsApi.create({
        data: {
          user_id: data['users.id'],
          vacancy_application_id: data.id,
          shopping_center_id: data?.vacancy?.shopping_center?.id,
        },
      });
    } catch (e) {
      console.log(e);
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    if (
      searchParams.get('by_shopping_center') &&
      !aditionalParamsForSingle?.params?.by_shopping_center
    ) {
      setAditionalParamsForSingle({
        params: {
          shopping_center_id: searchParams.get('by_shopping_center'),
        },
      });
    }
  }, [searchParams]);

  return {
    handleAddWishList,
    columns,
    data,
    filters,
    singleItem,
    loading,
    onDelete,
    updateInProcess,
    onChangeTableFilter,
    handleDownloadExcel,
    handleDownloadPdf,
    searchParams,
    pathname,
    navigate,
    statisticModalShow,
    setStatisticModalShow,
  };
};

export default useVacancyApplications;
